import { BASE_URL } from 'config';
import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import { Box, Avatar, Typography, CircularProgress } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";
import GiftSection from "./GiftSection";

const XYOfferSection = forwardRef((props, ref) => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productDetails, setProductDetails] = useState([]);
  const { error } = props;
  const giftSectionRef = useRef();

  // جلب المنتجات
  useEffect(() => {
    setProductsLoading(true);
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem("user-token");
        const response = await axios.get(BASE_URL + "/manager/products", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.data.status && Array.isArray(response.data.data)) {
          setProducts(response.data.data);
        } else {
          setProducts([]);
        }
      } catch (e) {
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // تحديث تفاصيل المنتجات المختارة
  useEffect(() => {
    setProductDetails(
      selectedProducts.map((product) => {
        const existing = productDetails.find((p) => p.id === product.id);
        return (
          existing || {
            id: product.id,
            name: product.name,
            image: product.medias && product.medias.length > 0 ? product.medias[0].file_name : "",
            minQty: 1,
            maxQty: 1,
          }
        );
      })
    );
    // eslint-disable-next-line
  }, [selectedProducts]);

  // دالة تحديث كمية منتج
  const handleProductQtyChange = (id, field, value) => {
    setProductDetails((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: Number(value) } : p))
    );
  };

  // دالة إرجاع بيانات العرض
  useImperativeHandle(ref, () => ({
    getXYData: () => {
      return {
        xy_products: productDetails.map((p) => ({
          product_id: p.id,
          min_quantity: p.minQty,
          max_quantity: p.maxQty,
        })),
      };
    },
    getGiftData: () => {
      return giftSectionRef.current
        ? giftSectionRef.current.getGiftData()
        : { rewards: { promotion_gift_types: [], free_products: [] } };
    },
    reset: () => {
      setSelectedProducts([]);
      setProductDetails([]);
      if (giftSectionRef.current) {
        giftSectionRef.current.reset();
      }
    },
  }));

  return (
    <MDBox
      display="flex"
      flexDirection="column"
      gap={3}
      p={{ xs: 1, sm: 2, md: 3 }}
      boxShadow={3}
      borderRadius={4}
      bgcolor="#fff"
      mb={1}
      sx={{
        width: { xs: "100%", md: "90%" },
        margin: "20px auto 0px auto",
        backgroundColor: "#fff !important",
        overflowX: "auto",
      }}
    >
      <MDTypography variant="h6" fontWeight="bold" mb={0.5}>
        بيانات عرض X Y
      </MDTypography>
      <Autocomplete
        multiple
        options={products}
        loading={productsLoading}
        getOptionLabel={(option) => option.name || ""}
        value={selectedProducts}
        onChange={(_, newValue) => setSelectedProducts(newValue)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={option.id}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Avatar
              src={option.medias && option.medias.length > 0 ? option.medias[0].file_name : ""}
              alt={option.name}
              sx={{ width: 32, height: 32 }}
            />
            <Typography variant="body2">{option.name}</Typography>
          </Box>
        )}
        renderInput={(params) => (
          <MDInput
            {...params}
            label="اختر منتجات العرض"
            fullWidth
            inputProps={{ ...params.inputProps, dir: "rtl" }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        sx={{ minWidth: { xs: "100%", md: 400 }, width: { xs: "100%", md: "auto" } }}
      />
      {error && (
        <Typography color="error" fontSize={13}>
          {error}
        </Typography>
      )}
      {productDetails.length > 0 && (
        <MDBox mt={2} sx={{ overflowX: "auto" }}>
          <MDTypography variant="subtitle1" fontWeight="bold" mb={1}>
            المنتجات المختارة:
          </MDTypography>
          <Box
            component="table"
            sx={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#fafafa",
              borderRadius: 2,
            }}
          >
            <Box component="thead">
              <Box component="tr" sx={{ background: "#f0f0f0" }}>
                <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                  المنتج
                </Box>
                <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                  أقل كمية
                </Box>
                <Box component="th" sx={{ p: 1, border: "1px solid #eee" }}>
                  أعلى كمية
                </Box>
              </Box>
            </Box>
            <Box component="tbody">
              {productDetails.map((p) => (
                <Box component="tr" key={p.id}>
                  <Box
                    component="td"
                    sx={{
                      p: 1,
                      border: "1px solid #eee",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Avatar src={p.image} alt={p.name} sx={{ width: 32, height: 32, ml: 1 }} />
                    <Typography variant="body2">{p.name}</Typography>
                  </Box>
                  <Box component="td" sx={{ p: 1, border: "1px solid #eee" }}>
                    <MDInput
                      type="number"
                      value={p.minQty}
                      onChange={(e) => handleProductQtyChange(p.id, "minQty", e.target.value)}
                      inputProps={{ min: 1, dir: "rtl" }}
                      sx={{ width: 80 }}
                    />
                  </Box>
                  <Box component="td" sx={{ p: 1, border: "1px solid #eee" }}>
                    <MDInput
                      type="number"
                      value={p.maxQty}
                      onChange={(e) => handleProductQtyChange(p.id, "maxQty", e.target.value)}
                      inputProps={{ min: 1, dir: "rtl" }}
                      sx={{ width: 80 }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </MDBox>
      )}
      <GiftSection ref={giftSectionRef} error={props.rewardsError} giftTypes={props.giftTypes} />
    </MDBox>
  );
});

export default XYOfferSection;
